import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { Button, Flex, Grid, GridCol, Space, Table, Text, UnstyledButton } from '@mantine/core';
import { IconBarcode, IconBox, IconCheck, IconEdit } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useBaleInboundContext } from '../hook';

export const BaleInboundRootPage = () => {
	const navigate = useNavigate();

	return (
		<MobileContainer
			header={
				<MobileHeader title="Bale Inbound" onClose={() => navigate({ to: '/facility/home' })} />
			}
		>
			<Flex direction="column" gap="md" pb={100} p="md">
				<DocketInfoSection />
				<LoadProcessingInfoSection />
			</Flex>
		</MobileContainer>
	);
};

const LoadProcessingInfoSection = () => {
	const { formData, scannedBales, setScannedBales, setPage, removeAllBales } =
		useBaleInboundContext();

	const baleCountMatch = useMemo(() => {
		return (
			formData.expectedBales.reduce((acc, bale) => acc + bale.count, 0) ===
			scannedBales.reduce((acc, bale) => acc + bale.count, 0)
		);
	}, [formData.expectedBales, scannedBales]);
	return (
		<Flex direction="column" gap="md">
			<Grid gutter="md">
				<GridCol span={6}>
					<Flex
						bg="white"
						w="100%"
						style={{
							borderRadius: 6,
							boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
							border: '1px solid #E0E0E0',
						}}
						direction="column"
						gap="lg"
						p="lg"
						align="center"
						justify="center"
						ta="center"
						pb="xl"
					>
						<Text size="sm" fw={600}>
							Expected
						</Text>
						<Text c="grey" style={{ fontSize: 62 }} fw={600}>
							{formData.expectedBales.reduce((acc, bale) => acc + bale.count, 0)}
						</Text>
					</Flex>
				</GridCol>
				<GridCol span={6}>
					<Flex
						bg="white"
						w="100%"
						style={{
							borderRadius: 6,
							boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
							border: '1px solid #E0E0E0',
						}}
						direction="column"
						gap="lg"
						p="lg"
						align="center"
						justify="center"
						ta="center"
						pb="xl"
					>
						<Text size="sm" fw={600}>
							Received
						</Text>
						<Text c={baleCountMatch ? 'green' : 'red'} style={{ fontSize: 62 }} fw={600}>
							{scannedBales.reduce((acc, bale) => acc + bale.count, 0)}
						</Text>
					</Flex>
				</GridCol>
			</Grid>
			<Flex direction="column" gap="md">
				{scannedBales.reduce((acc, bale) => acc + bale.count, 0) > 0 ? (
					<Flex direction="column" gap="sm">
						<Text size="sm" fw={600} mb="sm">
							Confirmed Bales
						</Text>
						<Table striped highlightOnHover>
							<Table.Tbody>
								{scannedBales
									.filter((bale) => bale.codes.length > 0)
									.flatMap((bale) =>
										bale.codes.map((code) => ({
											emoji: bale.emoji,
											name: bale.name,
											code: code,
										}))
									)
									.map((item, index) => (
										<Table.Tr bg="transparent" key={index}>
											<Table.Td>
												<Text size="sm" fw={500}>
													{item.emoji} {item.name}
												</Text>
											</Table.Td>
											<Table.Td>
												<Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
													{item.code}
												</Text>
											</Table.Td>
											<Table.Td w={40}>
												<IconCheck size={16} color="green" />
											</Table.Td>
										</Table.Tr>
									))}
							</Table.Tbody>
						</Table>
					</Flex>
				) : (
					<Flex
						direction="column"
						align="center"
						justify="center"
						p="lg"
						c="grey"
						gap="sm"
						style={{ border: '1px dashed rgb(178, 178, 178)', borderRadius: 6 }}
					>
						<IconBox strokeWidth={1.5} size={48} />
						<Text size="sm">You have not confirmed receipt of any bales yet</Text>
					</Flex>
				)}
				<Flex gap="sm">
					{scannedBales.reduce((acc, bale) => acc + bale.count, 0) > 0 ? (
						<Button
							bg="red"
							c="white"
							fullWidth
							variant="filled"
							onClick={removeAllBales}
							style={{ fontSize: 16 }}
							leftSection={<IconEdit size={16} />}
						>
							Remove Bales
						</Button>
					) : (
						<Button
							bg="grey"
							c="white"
							fullWidth
							variant="filled"
							onClick={() => setScannedBales(formData.expectedBales)}
							style={{ fontSize: 16 }}
							leftSection={<IconCheck size={16} />}
						>
							{`Confirm ${formData.expectedBales.reduce((acc, bale) => acc + bale.count, 0)} Bale${
								formData.expectedBales.reduce((acc, bale) => acc + bale.count, 0) > 1 ? 's' : ''
							}`}
						</Button>
					)}
					<Button
						fullWidth
						variant="filled"
						style={{ fontSize: 16 }}
						leftSection={<IconBarcode size={16} />}
						onClick={() => setPage('SCAN_ADD')}
					>
						Manual Scan
					</Button>
				</Flex>
			</Flex>
			<Space />
		</Flex>
	);
};

const DocketInfoSection = () => {
	const { setPage, formData } = useBaleInboundContext();

	return (
		<Flex
			bg="white"
			w="100%"
			style={{
				borderRadius: 6,
				boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
				border: '1px solid #E0E0E0',
			}}
		>
			<Flex direction="column" gap="xs" w="100%" p="md">
				<Flex justify="space-between" w="100%">
					<Text fw={600}>Load Details</Text>
					<UnstyledButton
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							fontSize: '16px',
							color: 'grey',
						}}
						onClick={() => setPage('DOCKET_EDIT')}
					>
						<IconEdit size={15} />
						Edit
					</UnstyledButton>
				</Flex>

				<Grid w="100%">
					<Grid.Col span={6}>
						<Flex direction="column" gap="xs">
							<Flex direction="column">
								<Text m={0} p={0} c="grey" size="xs">
									Docket ID
								</Text>
								<p
									style={{
										fontFamily: 'monospace',
										fontSize: 16,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{formData.docketId}
								</p>
							</Flex>
							<Flex direction="column">
								<Text m={0} p={0} c="grey" size="xs">
									Load Weight
								</Text>
								<p
									style={{
										fontFamily: 'monospace',
										fontSize: 16,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{formData.weight ? formData.weight / 1000 : 0}t
								</p>
							</Flex>
						</Flex>
					</Grid.Col>
					<Grid.Col span={6}>
						<Flex direction="column" gap="xs">
							<Flex direction="column">
								<Text m={0} p={0} c="grey" size="xs">
									Truck Registration
								</Text>
								<p
									style={{
										fontFamily: 'monospace',
										fontSize: 16,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{formData.truckRegistration}
								</p>
							</Flex>
						</Flex>
					</Grid.Col>
				</Grid>
			</Flex>
		</Flex>
	);
};
